import { API_ENDPOINTS } from '../config/apiConfig';

/**
 * Track when a user starts or resumes a test
 * @param {string} token - JWT token
 * @param {string} testType - 'practice' or 'mock'
 * @param {string} testName - Name of the test
 * @param {string} testCategory - Category of the test (optional)
 */
export const logTestAccess = async (token, testType, testName, testCategory = '') => {
  try {
    const response = await fetch(API_ENDPOINTS.TEST_ACCESS, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        testType,
        testName,
        testCategory
      })
    });

    if (!response.ok) {
      throw new Error('Failed to log test access');
    }

    const data = await response.json();
    return data.testHistory;
  } catch (error) {
    console.error('Error logging test access:', error);
    throw error;
  }
};

/**
 * Update test progress
 * @param {string} token - JWT token
 * @param {number} testId - Test history ID
 * @param {number} score - Score obtained (optional)
 * @param {number} totalQuestions - Total questions (optional)
 * @param {number} progressPercentage - Progress percentage
 * @param {string} status - 'started', 'in-progress', 'completed'
 */
export const updateTestProgress = async (
  token,
  testId,
  score = null,
  totalQuestions = null,
  progressPercentage = 0,
  status = 'in-progress'
) => {
  try {
    const response = await fetch(API_ENDPOINTS.TEST_PROGRESS, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        testId,
        score,
        totalQuestions,
        progressPercentage,
        status
      })
    });

    if (!response.ok) {
      throw new Error('Failed to update test progress');
    }

    const data = await response.json();
    return data.testHistory;
  } catch (error) {
    console.error('Error updating test progress:', error);
    throw error;
  }
};

/**
 * Track question attempts in batches to reduce API calls on serverless plans
 * @param {string} token - JWT token
 * @param {Array<{questionId: string|number, attemptCount?: number, failureCount?: number, successCount?: number}>} attempts
 * @param {string} testType - 'practice' or 'mock'
 * @param {string} testName - Name of the active test
 * @param {number|null} testHistoryId - Test history ID (optional)
 */
export const trackQuestionAttempts = async (
  token,
  attempts = [],
  testType = 'practice',
  testName = '',
  testHistoryId = null
) => {
  if (!Array.isArray(attempts) || attempts.length === 0) {
    return { processedAttempts: 0 };
  }

  try {
    const response = await fetch(API_ENDPOINTS.QUESTION_ATTEMPTS, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        attempts,
        testType,
        testName,
        testHistoryId
      })
    });

    if (!response.ok) {
      throw new Error('Failed to track question attempts');
    }

    return await response.json();
  } catch (error) {
    console.error('Error tracking question attempts:', error);
    throw error;
  }
};

/**
 * Fetch weak questions with the highest failure rates
 * @param {string} token - JWT token
 * @param {{limit?: number, minFailures?: number, testType?: string}} options
 */
export const fetchWeakQuestions = async (
  token,
  options = {}
) => {
  const {
    limit = 20,
    minFailures = 1,
    testType = 'practice'
  } = options;

  const query = new URLSearchParams({
    limit: String(limit),
    minFailures: String(minFailures),
    testType
  });

  try {
    const response = await fetch(`${API_ENDPOINTS.WEAK_QUESTIONS}?${query.toString()}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch weak questions');
    }

    const data = await response.json();
    return {
      weakQuestions: data.weakQuestions || [],
      count: data.count || 0
    };
  } catch (error) {
    console.error('Error fetching weak questions:', error);
    throw error;
  }
};

/**
 * Get recent tests with subscription-based limits
 * @param {string} token - JWT token
 * @param {number} limit - Number of tests to fetch
 */
export const fetchRecentTests = async (token, limit = 5) => {
  try {
    const response = await fetch(API_ENDPOINTS.RECENT_TESTS + `?limit=${limit}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch recent tests');
    }

    const data = await response.json();
    return {
      recentTests: data.recentTests,
      subscription: data.subscription,
      totalTests: data.totalTests
    };
  } catch (error) {
    console.error('Error fetching recent tests:', error);
    throw error;
  }
};

/**
 * Check if user can access unlimited content based on subscription
 * @param {string} subscription - User subscription type
 */
export const canAccessUnlimited = (subscription) => {
  return subscription === 'premium';
};

/**
 * Get max accessible items based on subscription
 * @param {string} subscription - User subscription type
 */
export const getMaxAccessibleItems = (subscription) => {
  return subscription === 'premium' ? Infinity : 3;
};

const testTracker = {
  logTestAccess,
  updateTestProgress,
  trackQuestionAttempts,
  fetchWeakQuestions,
  fetchRecentTests,
  canAccessUnlimited,
  getMaxAccessibleItems
};

export default testTracker;
